/**
 * Client-side scheduling utilities for filtering and processing time slots.
 * These functions are shared between the UI and command-line scripts.
 */

import { DaySlotType } from './scheduling'
import { AppointmentTypeType } from 'app/types/entities'
import { AvailabilityType } from 'app/types/entities'
import { ProviderType } from 'app/types/entities'

/**
 * Filters day slots by appointment type and provider.
 * Duration validation is handled server-side in generateAllSlots using actual
 * appointment data for accurate conflict detection.
 */
export function filterSlotsByAppointmentType(
  daySlots: DaySlotType[],
  appointmentType: AppointmentTypeType,
  providerId?: string,
): DaySlotType[] {
  return daySlots
    .filter((daySlot) => daySlot != null)
    .map((daySlot) => {
      return {
        ...daySlot,
        timeSlots: (daySlot.timeSlots || [])
          .map((timeSlot) => {
            let filteredAvailabilities = timeSlot.availabilities.filter((avail) =>
              avail.appointmentTypeIds?.includes(appointmentType.vendorId!),
            )

            if (providerId) {
              filteredAvailabilities = filteredAvailabilities.filter((avail) => avail.providerId === providerId)
            }

            return {
              ...timeSlot,
              availabilities: filteredAvailabilities,
            }
          })
          .filter((timeSlot) => timeSlot.availabilities.length > 0),
      }
    })
}

/**
 * Filters providers based on appointment type availability.
 * Returns only providers that have at least one availability supporting the appointment type.
 *
 * @param providers - All available providers
 * @param availabilities - All availabilities
 * @param appointmentType - The appointment type to filter by
 * @returns Providers that support the appointment type
 */
export function filterProvidersByAppointmentType(
  providers: ProviderType[],
  availabilities: AvailabilityType[],
  appointmentType: AppointmentTypeType,
): ProviderType[] {
  const relevantAvailabilities = availabilities.filter(
    (a) => a.appointmentTypeIds?.includes(appointmentType.vendorId!) && providers.find((p) => p.id === a.providerId),
  )
  return providers.filter((p) => relevantAvailabilities.some((a) => a.providerId === p.id))
}
